import { noAccessProvide } from "../../../components/Auth/AccessProvider";
import { extractSubscriptionFromAuthPayload } from "../../../components/Auth/SubscriptionHelpers";
import {
  logout,
  setLawerId,
  setSubscriptionState,
  setTeamData,
  setTeamMemberLawyerId,
  setToken,
  setUser,
  setUserAccess,
  setUserId,
} from "./userSlice";
import { normalizeAuthPayload, resolveAccessState } from "./sessionUtils";

export const AUTH_STORAGE_KEYS = Object.freeze({
  pendingAuth: "setLaterRes",
  persistedRoot: "persist:root",
  userData: "userData",
  userMeta: "UserId",
});

export const AUTH_STORAGE_SYNC_EVENT = "lexmom-auth-session-updated";

const getBrowserStorages = () => {
  if (typeof window === "undefined") {
    return [];
  }

  return [window.localStorage, window.sessionStorage].filter(Boolean);
};

const notifyStorageChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUTH_STORAGE_SYNC_EVENT));
};

const safeParse = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const readFromStorages = (key) => {
  const storages = getBrowserStorages();

  for (const storage of storages) {
    const value = safeParse(storage.getItem(key));
    if (value) {
      return value;
    }
  }

  return null;
};

const removeFromStorages = (key) => {
  getBrowserStorages().forEach((storage) => storage.removeItem(key));
};

const writeToStorages = (key, value) => {
  const serialized = JSON.stringify(value);
  getBrowserStorages().forEach((storage) => storage.setItem(key, serialized));
};

export const getPendingAuthSession = () =>
  readFromStorages(AUTH_STORAGE_KEYS.pendingAuth);

export const persistPendingAuthSession = (responseData) => {
  if (!responseData) {
    clearPendingAuthSession();
    return null;
  }

  writeToStorages(AUTH_STORAGE_KEYS.pendingAuth, responseData);
  notifyStorageChange();
  return responseData;
};

export const clearPendingAuthSession = () => {
  removeFromStorages(AUTH_STORAGE_KEYS.pendingAuth);
  notifyStorageChange();
};

export const getStoredUserMeta = () =>
  readFromStorages(AUTH_STORAGE_KEYS.userMeta);

export const getStoredAuthToken = () => getStoredUserMeta()?.token || null;

export const persistAuthenticatedSession = ({
  tokenValue,
  raw,
  userRecord,
}) => {
  if (!tokenValue) {
    return null;
  }

  writeToStorages(AUTH_STORAGE_KEYS.userMeta, {
    token: tokenValue,
    emailId:
      raw?.email ||
      userRecord?.email ||
      userRecord?.reminder_mail ||
      userRecord?.lawyer?.reminder_mail ||
      null,
    userType: "lawyer",
  });

  if (userRecord) {
    writeToStorages(AUTH_STORAGE_KEYS.userData, userRecord);
  } else {
    removeFromStorages(AUTH_STORAGE_KEYS.userData);
  }

  notifyStorageChange();
  return tokenValue;
};

export const clearStoredAuthSession = () => {
  removeFromStorages(AUTH_STORAGE_KEYS.userMeta);
  removeFromStorages(AUTH_STORAGE_KEYS.userData);
  clearPendingAuthSession();
  notifyStorageChange();
};

export const hasStoredAuthArtifacts = () =>
  getBrowserStorages().some((storage) =>
    Object.values(AUTH_STORAGE_KEYS).some((key) => !!storage.getItem(key)),
  );

export const hasCommittedAuthArtifacts = () =>
  getBrowserStorages().some(
    (storage) =>
      !!storage.getItem(AUTH_STORAGE_KEYS.userMeta) ||
      !!storage.getItem(AUTH_STORAGE_KEYS.userData),
  );

export const applyAuthSession = (dispatch, responseData, options = {}) => {
  const { clearPending = false, persistSession = true } = options;

  if (!dispatch || !responseData) {
    return null;
  }

  const normalized = normalizeAuthPayload(responseData);
  const {
    tokenValue,
    teamData,
    userRecord,
    lawyerId,
    userId,
    raw,
  } = normalized;

  if (!tokenValue) {
    return null;
  }

  dispatch(setToken(tokenValue));
  dispatch(setTeamData(teamData));
  dispatch(setUser(userRecord));
  dispatch(setLawerId(lawyerId || null));
  dispatch(setUserId(userId || null));

  const { finalAccess, memberId } = resolveAccessState(
    teamData,
    noAccessProvide,
  );

  dispatch(setUserAccess(finalAccess));
  dispatch(setTeamMemberLawyerId(memberId || null));
  dispatch(setSubscriptionState(extractSubscriptionFromAuthPayload(responseData)));

  if (persistSession) {
    persistAuthenticatedSession({ tokenValue, raw, userRecord });
  }

  if (clearPending) {
    clearPendingAuthSession();
  }

  return {
    ...normalized,
    memberId: memberId || null,
    tokenValue,
  };
};

export const clearAuthSession = ({
  dispatch,
  purgePersistor,
  clearAllStorage = false,
} = {}) => {
  if (dispatch) {
    dispatch(logout());
  }

  if (typeof purgePersistor === "function") {
    Promise.resolve(purgePersistor()).catch(() => {});
  }

  if (typeof window !== "undefined") {
    if (clearAllStorage) {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } else {
      clearStoredAuthSession();
      removeFromStorages(AUTH_STORAGE_KEYS.persistedRoot);
    }
  }

  notifyStorageChange();
};
